import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { useNavigate } from "react-router-dom";

import ProfileNavigation from "../../../components/common/ProfileNavigation";
import ErrorHandler from "../../../components/common/ErrorHandler";
import SEO from "../../../components/SEO";
import useUser, { getAuthorized, isUserClient, isUserDoctor, postAuthorized } from "../../../hooks/User";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import { convertToUserTimezone, formatTimeInUserTimezone } from "../../../utils/timezone";
import { ROUTES } from "../../../routes";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    PillIcon,
    CalendarIcon,
    CheckIcon,
    XIcon,
    BellIcon,
    HistoryIcon,
    SnoozeIcon,
    PrinterIcon
} from "../../../components/icons";

import "./mymedicationcalendar.css";

dayjs.locale("ru");

// Status configs
const EventStatusConfig = {
    pending: { label: "Ожидает", className: "status-pending", icon: "⏳" },
    sent: { label: "Требует ответа", className: "status-sent", icon: "🔔" },
    snoozed: { label: "Отложено", className: "status-snoozed", icon: "⏰" },
    responded: { label: "Отвечено", className: "status-responded", icon: "✓" },
    missed: { label: "Пропущено", className: "status-missed", icon: "⚠️" }
};

// Header with month navigation
function CalendarHeader({ currentMonth, onPrev, onNext, onToday }) {
    const monthLabel = currentMonth.format("MMMM YYYY");

    return (
        <div className="calendar-header-section">
            <div className="calendar-title">
                <CalendarIcon size={32} />
                <div>
                    <h1>Календарь приёма лекарств</h1>
                    <p className="calendar-subtitle">Расписание приёма и статусы по дням</p>
                </div>
            </div>
            <div className="calendar-controls">
                <button className="btn-calendar-control" onClick={onPrev} title="Предыдущий месяц">
                    <ChevronLeftIcon />
                </button>
                <span className="current-month">{monthLabel}</span>
                <button className="btn-calendar-control" onClick={onNext} title="Следующий месяц">
                    <ChevronRightIcon />
                </button>
                <button className="btn-today" onClick={onToday}>
                    Сегодня
                </button>
            </div>
        </div>
    );
}

// Day cell event chip
function EventChip({ event, onRespond, onSnooze, respondingId, snoozingId }) {
    const status = EventStatusConfig[event.status] || EventStatusConfig.pending;
    const isTaken = event.intake_status === "taken";
    const isSkipped = event.intake_status === "skipped";

    return (
        <div className={`event-chip ${status.className} ${isTaken ? "intake-taken" : ""} ${isSkipped ? "intake-skipped" : ""}`}>
            <div className="event-chip-main">
                <ClockIcon size={14} />
                <span className="event-time">{formatTimeInUserTimezone(event.dose_time)}</span>
                <span className="event-name" title={event.medication_name}>
                    {event.medication_name}
                </span>
                <span className="event-dosage">{event.dosage}</span>
            </div>
            <div className="event-chip-actions">
                {event.can_respond && (
                    <>
                        <button
                            className="event-btn event-btn-taken"
                            onClick={() => onRespond(event, "taken")}
                            disabled={respondingId === event.reminder_id}
                            title="Принял"
                        >
                            <CheckIcon size={14} />
                        </button>
                        <button
                            className="event-btn event-btn-skipped"
                            onClick={() => onRespond(event, "skipped")}
                            disabled={respondingId === event.reminder_id}
                            title="Пропустил"
                        >
                            <XIcon size={14} />
                        </button>
                    </>
                )}
                {event.can_snooze && (
                    <button
                        className="event-btn event-btn-snooze"
                        onClick={() => onSnooze(event)}
                        disabled={snoozingId === event.reminder_id}
                        title="Напомнить позже"
                    >
                        <SnoozeIcon size={14} />
                    </button>
                )}
            </div>
        </div>
    );
}

// Day cell in month grid
function DayCell({ date, events, isToday, isCurrentMonth, onRespond, onSnooze, respondingId, snoozingId }) {
    const dayNumber = date.date();

    return (
        <div className={`day-cell ${isToday ? "today" : ""} ${!isCurrentMonth ? "outside-month" : ""}`}>
            <div className="day-cell-header">
                <span className="day-number">{dayNumber}</span>
                {isToday && <span className="today-badge">Сегодня</span>}
            </div>
            <div className="day-cell-events">
                {events.map((event, index) => (
                    <EventChip
                        key={`${event.schedule_id}-${index}`}
                        event={event}
                        onRespond={onRespond}
                        onSnooze={onSnooze}
                        respondingId={respondingId}
                        snoozingId={snoozingId}
                    />
                ))}
            </div>
        </div>
    );
}

// Respond modal for calendar events
function ResponseModal({ event, responseType, onClose, onSubmit, isLoading }) {
    const [formData, setFormData] = useState({
        actual_dosage: event?.dosage || "",
        taken_at_minutes_ago: 0,
        notes: "",
        skip_reason: "",
        skip_reason_category: "other"
    });

    if (!event) return null;
    const isSkipped = responseType === "skipped";

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = { response_type: responseType, ...formData };
        if (!isSkipped) {
            delete payload.skip_reason;
            delete payload.skip_reason_category;
        }
        onSubmit(event.reminder_id, payload);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isSkipped ? "Пропуск приёма" : "Подтверждение приёма"}</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="reminder-summary">
                            <div className="summary-icon">
                                <PillIcon />
                            </div>
                            <div className="summary-info">
                                <h4>{event.medication_name}</h4>
                                <p>{event.dosage}</p>
                                <p className="summary-time">
                                    {convertToUserTimezone(event.dose_time).format("DD.MM.YYYY HH:mm")}
                                </p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{isSkipped ? "Фактическая доза" : "Принятая доза"}</label>
                            <input
                                type="text"
                                value={formData.actual_dosage}
                                onChange={(e) => setFormData((prev) => ({ ...prev, actual_dosage: e.target.value }))}
                                placeholder="Например: 1 таблетка"
                            />
                        </div>

                        {!isSkipped && (
                            <div className="form-group">
                                <label>Принято (минут назад)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="120"
                                    value={formData.taken_at_minutes_ago}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, taken_at_minutes_ago: parseInt(e.target.value) || 0 }))}
                                />
                            </div>
                        )}

                        {isSkipped && (
                            <>
                                <div className="form-group">
                                    <label>Причина пропуска *</label>
                                    <select
                                        value={formData.skip_reason_category}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, skip_reason_category: e.target.value }))}
                                        required
                                    >
                                        <option value="forgot">Забыл</option>
                                        <option value="not_available">Нет лекарства</option>
                                        <option value="side_effects">Побочные эффекты</option>
                                        <option value="feeling_bad">Плохое самочувствие</option>
                                        <option value="other">Другое</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Описание причины</label>
                                    <textarea
                                        value={formData.skip_reason}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, skip_reason: e.target.value }))}
                                        placeholder="Уточните причину пропуска..."
                                        rows={2}
                                    />
                                </div>
                            </>
                        )}

                        <div className="form-group">
                            <label>Заметки</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                                placeholder="Дополнительные заметки..."
                                rows={2}
                            />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
                            Отмена
                        </button>
                        <button
                            type="submit"
                            className={`btn-primary ${isSkipped ? "btn-danger" : "btn-success"}`}
                            disabled={isLoading}
                        >
                            {isLoading ? "Сохранение..." : (
                                <>
                                    {isSkipped ? <XIcon size={16} /> : <CheckIcon size={16} />}
                                    <span>{isSkipped ? "Подтвердить пропуск" : "Подтвердить приём"}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Month calendar grid
function MonthCalendar({ currentMonth, calendarDays, eventsByDate, onRespond, onSnooze, respondingId, snoozingId }) {
    const weekDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
    const today = dayjs().startOf("day");

    return (
        <div className="month-calendar">
            <div className="weekday-header">
                {weekDays.map((day) => (
                    <div key={day} className="weekday-cell">{day}</div>
                ))}
            </div>
            <div className="days-grid">
                {calendarDays.map((date) => (
                    <DayCell
                        key={date.format("YYYY-MM-DD")}
                        date={date}
                        events={eventsByDate[date.format("YYYY-MM-DD")] || []}
                        isToday={date.isSame(today, "day")}
                        isCurrentMonth={date.isSame(currentMonth, "month")}
                        onRespond={onRespond}
                        onSnooze={onSnooze}
                        respondingId={respondingId}
                        snoozingId={snoozingId}
                    />
                ))}
            </div>
        </div>
    );
}

// Legend
function CalendarLegend() {
    return (
        <div className="calendar-legend">
            <span className="legend-title">Статусы:</span>
            {Object.entries(EventStatusConfig).map(([key, config]) => (
                <div key={key} className={`legend-item ${config.className}`}>
                    <span className="legend-icon">{config.icon}</span>
                    <span>{config.label}</span>
                </div>
            ))}
        </div>
    );
}

// Main calendar component
function MyMedicationCalendar() {
    const { setErrorList } = useContext(ErrorHandlerContext);
    const navigate = useNavigate();

    const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
    const [calendarData, setCalendarData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [respondingId, setRespondingId] = useState(null);
    const [snoozingId, setSnoozingId] = useState(null);
    const [responseModal, setResponseModal] = useState({
        isOpen: false,
        event: null,
        responseType: null
    });

    const calendarDays = useMemo(() => {
        const startOfMonth = currentMonth.startOf("month");
        const endOfMonth = currentMonth.endOf("month");
        const startDay = startOfMonth.day() || 7; // Monday = 1
        const startDate = startOfMonth.subtract(startDay - 1, "day");
        const endDay = endOfMonth.day() || 7;
        const endDate = endOfMonth.add(7 - endDay, "day");

        const days = [];
        let date = startDate;
        while (date.isBefore(endDate) || date.isSame(endDate, "day")) {
            days.push(date);
            date = date.add(1, "day");
        }
        return days;
    }, [currentMonth]);

    const loadCalendar = useCallback(async () => {
        setLoading(true);
        try {
            const fromDate = currentMonth.startOf("month").format("YYYY-MM-DD");
            const toDate = currentMonth.endOf("month").format("YYYY-MM-DD");
            const response = await getAuthorized("/client/my_medication_calendar", {
                params: { from_date: fromDate, to_date: toDate }
            });
            setCalendarData(response.data?.days || []);
        } catch (error) {
            console.error("Failed to load medication calendar:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при загрузке календаря"]);
        } finally {
            setLoading(false);
        }
    }, [currentMonth, setErrorList]);

    useEffect(() => {
        loadCalendar();
    }, [loadCalendar]);

    const eventsByDate = useMemo(() => {
        const map = {};
        calendarData.forEach((day) => {
            map[day.date] = day.events || [];
        });
        return map;
    }, [calendarData]);

    const handlePrevMonth = () => setCurrentMonth((prev) => prev.subtract(1, "month"));
    const handleNextMonth = () => setCurrentMonth((prev) => prev.add(1, "month"));
    const handleToday = () => setCurrentMonth(dayjs().startOf("month"));

    const handleRespondClick = useCallback((event, responseType) => {
        setResponseModal({ isOpen: true, event, responseType });
    }, []);

    const handleRespondSubmit = useCallback(async (reminderId, payload) => {
        setRespondingId(reminderId);
        try {
            await postAuthorized(`/client/respond_medication_reminder/${reminderId}`, payload);
            setResponseModal({ isOpen: false, event: null, responseType: null });
            loadCalendar();
        } catch (error) {
            console.error("Failed to respond:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при сохранении ответа"]);
        } finally {
            setRespondingId(null);
        }
    }, [loadCalendar, setErrorList]);

    const handleSnooze = useCallback(async (event) => {
        if (!event.reminder_id) return;
        setSnoozingId(event.reminder_id);
        try {
            await postAuthorized(`/client/snooze_medication_reminder/${event.reminder_id}/snooze`);
            loadCalendar();
        } catch (error) {
            console.error("Failed to snooze:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при откладывании"]);
        } finally {
            setSnoozingId(null);
        }
    }, [loadCalendar, setErrorList]);

    const handleCloseModal = useCallback(() => {
        setResponseModal({ isOpen: false, event: null, responseType: null });
    }, []);

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const totalEvents = calendarData.reduce((sum, day) => sum + (day.events?.length || 0), 0);
    const respondedEvents = calendarData.reduce(
        (sum, day) => sum + (day.events?.filter((e) => e.response_status === "responded").length || 0),
        0
    );

    return (
        <div className="my-medication-calendar-container">
            <CalendarHeader
                currentMonth={currentMonth}
                onPrev={handlePrevMonth}
                onNext={handleNextMonth}
                onToday={handleToday}
            />

            <div className="calendar-toolbar">
                <div className="calendar-stats">
                    <span>Всего приёмов: <strong>{totalEvents}</strong></span>
                    <span>Отвечено: <strong>{respondedEvents}</strong></span>
                </div>
                <div className="calendar-view-switch">
                    <button className="btn-view-switch" onClick={() => navigate(ROUTES.MY_MEDICATIONS)}>
                        <BellIcon size={18} />
                        Напоминания
                    </button>
                    <button className="btn-view-switch active">
                        <CalendarIcon size={18} />
                        Календарь
                    </button>
                    <button className="btn-view-switch" onClick={() => navigate(ROUTES.MEDICATION_HISTORY)}>
                        <HistoryIcon size={18} />
                        История
                    </button>
                    <button className="btn-view-switch btn-print" onClick={handlePrint}>
                        <PrinterIcon size={18} />
                        Печать
                    </button>
                </div>
            </div>

            <CalendarLegend />

            {loading ? (
                <div className="calendar-loading">
                    <div className="spinner"></div>
                    <p>Загрузка календаря...</p>
                </div>
            ) : totalEvents === 0 ? (
                <div className="empty-calendar-state">
                    <div className="empty-icon">
                        <CalendarIcon size={40} />
                    </div>
                    <h3>Нет назначенных приёмов</h3>
                    <p>На выбранный месяц нет назначений лекарств</p>
                </div>
            ) : (
                <MonthCalendar
                    currentMonth={currentMonth}
                    calendarDays={calendarDays}
                    eventsByDate={eventsByDate}
                    onRespond={handleRespondClick}
                    onSnooze={handleSnooze}
                    respondingId={respondingId}
                    snoozingId={snoozingId}
                />
            )}

            <ResponseModal
                event={responseModal.event}
                responseType={responseModal.responseType}
                isOpen={responseModal.isOpen}
                onClose={handleCloseModal}
                onSubmit={handleRespondSubmit}
                isLoading={respondingId !== null}
            />
        </div>
    );
}

function MyMedicationCalendarPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, error, loading, navigate]);

    if (loading) {
        return (
            <>
                <SEO title="Календарь лекарств" description="Календарь приёма лекарств" noindex={true} />
                <ProfileNavigation isDoctor={false} />
                <div className="my-medication-calendar-page">
                    <div className="calendar-loading">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title="Календарь лекарств" description="Календарь приёма лекарств" noindex={true} />
            <ErrorHandlerContextProvider>
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="my-medication-calendar-page">
                    <MyMedicationCalendar />
                </div>
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export default MyMedicationCalendarPage;
